import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';
import Modal from '../Modal';
import { fmtDateTime } from '../../lib/dates';

// Admin review modal for a pending document_acknowledgement row.
// Shows:
//   - supplier name + doc name + signed-by name/email + signed-at
//   - the doc content (richtext or file iframe) so the admin can see
//     what the supplier signed against
//   - Approve / Reject buttons
//
// On Approve:
//   - sets review_decision='approved', reviewed_at=now, reviewed_by
//   - The supplier's hook synthesises this row into APPROVED state on
//     next refetch.
//
// On Reject (requires reason notes):
//   - sets review_decision='rejected', reviewed_at, reviewed_by,
//     review_notes=<reason>
//   - The supplier's hook synthesises this back to OUTSTANDING so they
//     can re-acknowledge.
//
// Phase 5 will hook compliance_audit_log + Recent Activity entries to
// each transition; for now the DB update is the only side-effect.

export default function AcknowledgementReviewModal({
  open,
  ack,             // document_acknowledgements row
  docMeta,         // { document_name, content_richtext, content_file_url, source } from fusion_documents
  supplierName,
  reviewerId,
  onClose,
  onActioned,
}) {
  const [busy, setBusy] = useState(null);  // 'approve' | 'reject' | null
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [error, setError] = useState(null);

  if (!open || !ack || !docMeta) return null;

  const approve = async () => {
    setError(null);
    setBusy('approve');
    const { error: updErr } = await supabase
      .from('document_acknowledgements')
      .update({
        review_decision: 'approved',
        reviewed_at: new Date().toISOString(),
        reviewed_by: reviewerId || null,
      })
      .eq('id', ack.id);
    if (updErr) { setBusy(null); setError(updErr.message || 'Approval failed.'); return; }
    // Audit trail (Phase 5) — surfaces on the SupplierDetail Recent
    // Activity feed as "Acknowledgement approved · {doc}".
    await supabase.from('compliance_audit_log').insert([{
      actor_id: reviewerId || null,
      action: 'acknowledge_approved',
      supplier_id: ack.supplier_id,
      document_key: ack.document_key,
      meta: {
        document_label: docMeta?.document_name || null,
        acknowledgement_id: ack.id,
        document_version: ack.document_version,
        signer_name: ack.signer_name,
      },
    }]);
    setBusy(null);
    onActioned?.();
    onClose();
  };

  const reject = async () => {
    const clean = String(rejectReason || '').trim();
    if (!clean) {
      setError('Add a reason so the supplier knows what to fix.');
      return;
    }
    setError(null);
    setBusy('reject');
    const { error: updErr } = await supabase
      .from('document_acknowledgements')
      .update({
        review_decision: 'rejected',
        reviewed_at: new Date().toISOString(),
        reviewed_by: reviewerId || null,
        review_notes: clean,
      })
      .eq('id', ack.id);
    if (updErr) { setBusy(null); setError(updErr.message || 'Rejection failed.'); return; }
    // Audit trail (Phase 5) — captures the rejection reason in meta
    // so the activity feed can show why without joining back to
    // document_acknowledgements.
    await supabase.from('compliance_audit_log').insert([{
      actor_id: reviewerId || null,
      action: 'acknowledge_rejected',
      supplier_id: ack.supplier_id,
      document_key: ack.document_key,
      meta: {
        document_label: docMeta?.document_name || null,
        acknowledgement_id: ack.id,
        document_version: ack.document_version,
        signer_name: ack.signer_name,
        review_notes: clean,
      },
    }]);
    setBusy(null);
    onActioned?.();
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} maxWidth={760} ariaLabel={`Review acknowledgement for ${docMeta.document_name}`}>
      <Modal.Header
        eyebrow="Acknowledgement review"
        title={docMeta.document_name}
        sub={`Signed by ${ack.signer_name || '—'} (${supplierName || 'supplier'}) on ${fmtDateTime(ack.acknowledged_at)}.`}
        onClose={onClose} />
      <Modal.Body>
        {/* Document content — same iframe / HTML pattern as the
            supplier-side modal so the admin sees the same thing the
            supplier read before signing. */}
        <div className="bg-white border border-line rounded-lg p-4 max-h-[360px] overflow-y-auto overflow-x-hidden min-w-0 max-w-full mb-5">
          {docMeta.source === 'file' && docMeta.content_file_url ? (
            <iframe src={docMeta.content_file_url} title={docMeta.document_name} className="w-full h-[320px] bg-white rounded" />
          ) : docMeta.content_richtext ? (
            <div
              // prose-compliance = the shared document-body style (index.css).
              // Same class as the supplier's modal so the admin reads exactly
              // what the supplier read, wrapped as paragraphs.
              className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed prose-compliance"
              dangerouslySetInnerHTML={{ __html: docMeta.content_richtext }}
            />
          ) : (
            <p className="font-['Metropolis'] text-sm text-[#51534A] italic">No content configured on this document.</p>
          )}
        </div>

        {/* Sign-off audit panel — supplier-side proof of what was
            captured. */}
        <div className="bg-white border border-line rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A] mb-1">Signed by</div>
              <div className="font-['Metropolis'] text-sm text-ink font-semibold">{ack.signer_name || '—'}</div>
              {ack.signer_email && (
                <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">{ack.signer_email}</div>
              )}
            </div>
            <div>
              <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A] mb-1">Acknowledged at</div>
              <div className="font-['Metropolis'] text-sm text-ink font-semibold">{fmtDateTime(ack.acknowledged_at)}</div>
              {ack.document_version && (
                <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">Version: {ack.document_version}</div>
              )}
            </div>
          </div>
          {/* The signature itself. Reviewing a signed document without seeing
              the signature is reviewing a claim that one exists. Rows signed
              before drawn signatures existed say so plainly rather than
              showing an empty box. */}
          <div className="mt-4 pt-4 border-t border-[#F0F2E8]">
            <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A] mb-1.5">Signature</div>
            {ack.signature_url ? (
              <img
                src={ack.signature_url}
                alt={`Signature of ${ack.signer_name || 'the signer'}`}
                className="bg-white border border-line rounded-md max-h-[120px] w-auto" />
            ) : (
              <p className="font-['Metropolis'] text-[12px] text-[#51534A] italic">
                Typed name only — signed before drawn signatures were introduced.
              </p>
            )}
          </div>
        </div>

        {/* Reject inline reason — appears when admin clicks Reject. */}
        {showRejectInput && (
          <div className="bg-[#E0290A]/[0.04] border border-[#E0290A]/30 rounded-lg p-3 mb-3">
            <label className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#E0290A] mb-1.5 block">
              Reason for rejection
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="What does the supplier need to fix before re-acknowledging?"
              rows={3}
              className="w-full bg-white border border-line rounded-md px-3 py-2 text-sm text-ink outline-none font-['Metropolis'] focus:border-[#E0290A] resize-y"
            />
            <p className="font-['Metropolis'] text-[10px] text-[#51534A] mt-1.5">
              Shown to the supplier in their portal. Required before reject.
            </p>
          </div>
        )}

        {error && <p className="font-['Metropolis'] text-[12px] text-[#E0290A] font-semibold mb-2">{error}</p>}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-between items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy != null}
            className="bg-white border border-line rounded font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-ink px-4 py-2 hover:border-[#DADCCD] transition-colors disabled:opacity-50">
            Close
          </button>
          <div className="flex gap-2">
            {showRejectInput ? (
              <>
                <button
                  type="button"
                  onClick={() => { setShowRejectInput(false); setRejectReason(''); setError(null); }}
                  disabled={busy != null}
                  className="bg-white border border-line rounded font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-fusion-mid-grey px-4 py-2 hover:border-[#DADCCD] transition-colors disabled:opacity-50">
                  Cancel reject
                </button>
                <button
                  type="button"
                  onClick={reject}
                  disabled={busy != null || !rejectReason.trim()}
                  className="bg-white border border-[#E0290A] text-[#E0290A] rounded font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase px-4 py-2 hover:bg-[#E0290A]/10 transition-colors disabled:opacity-50">
                  {busy === 'reject' ? 'Rejecting…' : 'Confirm reject'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setShowRejectInput(true)}
                  disabled={busy != null}
                  className="bg-white border border-[#E0290A] text-[#E0290A] rounded font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase px-4 py-2 hover:bg-[#E0290A]/10 transition-colors disabled:opacity-50">
                  Reject
                </button>
                <button
                  type="button"
                  onClick={approve}
                  disabled={busy != null}
                  className="bg-[#92D400] border border-[#92D400] rounded font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-[#131313] px-4 py-2 hover:bg-[#a4e810] transition-colors disabled:opacity-50">
                  {busy === 'approve' ? 'Approving…' : 'Approve'}
                </button>
              </>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
